"use client";

// ─────────────────────────────────────────────────────────────────────────
// A purchase that has not landed by the time the return page runs out of
// patience is still a purchase. Keep watching it.
//
// THE REPORTED BUG. A PayPal trial return showed the amber "we're still
// finishing your trial setup" notice and then nothing ever happened: the nav
// chip stayed on the free daily cap, no confirmation appeared, and the only way
// to see the trial that HAD gone live was to reload the page by hand. The copy
// even said so — "Refresh in a moment" — which is the application asking the
// user to do its polling for it.
//
// The state was never missing. It arrives a few seconds later, and the app
// already owns a mechanism that notices: syncEntitlementUntilSettled polls
// /api/credits + /api/billing/overview (both of which SELF-HEAL a missed
// webhook server-side, so polling is what advances fulfilment, not merely what
// observes it) and broadcasts every round on the same bus the nav chip, the
// trial card and the billing card already listen to. The checkout-return path
// simply never handed off to it — it decided once and gave up.
//
// So this module is the handoff, not a new mechanism: one place that says what
// "landed" means for each purchase kind, how long to keep looking, and which
// of the two honest sentences to show. Every return surface calls it, so a
// sixth one cannot reintroduce "please refresh".
// ─────────────────────────────────────────────────────────────────────────

import { customToast } from "@/lib/toast";
import { showPurchaseConfirmation } from "@/components/billing/purchase-confirmation-store";
import type { PurchaseConfirmationInput } from "@/lib/billing/purchase-confirmation";
import {
  broadcastCredits,
  syncEntitlementUntilSettled,
  type EntitlementSnapshot,
} from "@/lib/billing/entitlement-refresh";
import {
  decideConfirmation,
  trialPendingMessage,
  trialUnresolvedMessage,
  upgradePendingMessage,
  upgradeUnresolvedMessage,
  type CheckoutReturnType,
  type ReconcileResult,
  type EntitlementSnapshot as CreditsSnapshot,
} from "@/lib/billing/checkout-return";

/**
 * Roughly two minutes, front-loaded then backing off.
 *
 * Longer than the return page's own ~28s budget on purpose: that budget blocks
 * a spinner, so it has to end quickly, while this runs in the background behind
 * a notice the user can ignore. Production fulfilment was measured at ~10s, so
 * the first few rounds carry almost every real case; the long tail is for a
 * webhook that was retried rather than merely slow.
 */
export const PENDING_WATCH_BACKOFF_MS: readonly number[] = [
  0, 3_000, 5_000, 8_000, 10_000, 15_000, 20_000, 30_000, 30_000,
];

export type PendingPurchase = "trial" | "upgrade";

/**
 * A started trial is visible as the trial flag OR as a plan that is no longer
 * free — either one means start_trial ran. Before it runs the account reads
 * exactly as it did pre-purchase, which is the whole reason a single read
 * cannot tell "not yet" from "never".
 */
export function isTrialLive(snapshot: EntitlementSnapshot): boolean {
  return (
    snapshot.isTrialing === true ||
    (typeof snapshot.plan === "string" && snapshot.plan !== "free")
  );
}

/**
 * A pending UPGRADE is the mirror image: decideConfirmation withholds the
 * confirmation only while the account can still be positively SEEN on its
 * trial, so that same condition clearing is what settles the watch. Stated
 * against the decision rule deliberately — two different answers to "is this
 * done?" would put a confirmation on screen the decision would have withheld.
 */
export function isPlanLive(snapshot: EntitlementSnapshot): boolean {
  return snapshot.isTrialing !== true;
}

/**
 * Poll until the purchase is visible, broadcasting each round so the nav chip
 * and every other entitlement surface converge without a reload.
 *
 * Resolves true when it landed, false when the watch expired. Never throws:
 * every caller is post-payment, and a failure to WATCH must not be reported as
 * a failure to PAY.
 */
export async function watchPendingPurchase(
  kind: PendingPurchase,
): Promise<boolean> {
  const { settled } = await syncEntitlementUntilSettled({
    isSettled: kind === "trial" ? isTrialLive : isPlanLive,
    backoffMs: PENDING_WATCH_BACKOFF_MS,
  });
  return settled;
}

/** What the return surface already knows about the purchase it is waiting on. */
export interface PendingReturnContext {
  returnType: CheckoutReturnType;
  reconcile: ReconcileResult | null;
  source: string;
  projectId?: string | null;
}

/**
 * Re-run the decision that came back pending, now that the entitlement is
 * visible.
 *
 * decideConfirmation stays the single owner of "what does this purchase
 * confirm as" — the watch only changes WHEN we ask it, never what it may
 * answer. Re-reading the entitlement rather than reusing the stale one is the
 * point: the stale read is precisely the thing that said "not yet".
 */
export async function resolveSettledReturn(
  ctx: PendingReturnContext,
): Promise<PurchaseConfirmationInput | null> {
  const fresh = (await fetch("/api/credits", { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null)) as CreditsSnapshot | null;
  // Push it at the nav chip before deciding. The chip reads /api/credits once
  // on mount and never again, so a confirmation modal saying "You're on the Pro
  // trial" next to a chip still saying "50/50 credits" is not a race — it is
  // the return path never telling anyone what it just read.
  broadcastCredits(fresh);
  const decision = decideConfirmation({ ...ctx, fresh });
  return decision.status === "confirm" ? decision.input : null;
}

/**
 * The whole pending experience for a surface that speaks in toasts: say what
 * is happening, watch for it to land, then either confirm the purchase for real
 * or hand the user to support.
 *
 * Resolves to whether the purchase landed, so a caller with more to do than
 * open a modal can act on it.
 */
export async function announcePendingPurchase(
  args: PendingReturnContext & {
    kind: PendingPurchase;
    /** Runs INSTEAD of the default modal once the purchase lands. */
    onConfirmed?: (input: PurchaseConfirmationInput) => void;
  },
): Promise<boolean> {
  const { kind, onConfirmed, ...ctx } = args;
  customToast.info(
    kind === "trial" ? trialPendingMessage() : upgradePendingMessage(),
  );

  if (await watchPendingPurchase(kind)) {
    const input = await resolveSettledReturn(ctx);
    // Settled but unconfirmable is not a contradiction: the watch reads the
    // billing overview and the confirmation reads /api/credits, so the two can
    // be a beat apart. The purchase is live either way and every surface has
    // already been updated by the broadcast — there is simply no modal to show.
    if (input) (onConfirmed ?? showPurchaseConfirmation)(input);
    return true;
  }

  customToast.info(
    kind === "trial" ? trialUnresolvedMessage() : upgradeUnresolvedMessage(),
  );
  return false;
}
